import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { Coords } from '@/types/widget'
import { createPersistOptions } from '@/utils/zustand-persist'

interface UserDeviceLocationState {
  coords: Coords | undefined
  loading: boolean
  error: string | undefined
  permissionDenied: boolean
  attemptedAutoLocate: boolean
}

interface LocateUserOptions {
  force?: boolean
}

interface UserDeviceLocationActions {
  locateUser: (options?: LocateUserOptions) => Promise<Coords | undefined>
}

type UserDeviceLocationStore = UserDeviceLocationState & UserDeviceLocationActions

const initialState: UserDeviceLocationState = {
  coords: undefined,
  loading: false,
  error: undefined,
  permissionDenied: false,
  attemptedAutoLocate: false,
}

const PERSIST_NAME = 'user-device-location'
const PERSIST_STORAGE_KEY = `widget-ui:${PERSIST_NAME}`

type PersistedLocationSlice = Pick<
  UserDeviceLocationState,
  'coords' | 'permissionDenied' | 'attemptedAutoLocate'
>

function readPersistedLocationState(): PersistedLocationSlice | undefined {
  if (typeof sessionStorage === 'undefined') return undefined
  try {
    const raw = sessionStorage.getItem(PERSIST_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as { state?: PersistedLocationSlice }
    return parsed?.state
  } catch {
    return undefined
  }
}

/**
 * Sync-read sessionStorage into the store before async zustand rehydration can finish.
 * Needed because widget init may call locateUser / getUserDeviceLocation on the first tick
 * of a full page load (MPA navigations).
 */
export function hydrateUserDeviceLocationFromSession(): void {
  const current = useUserDeviceLocationStore.getState()
  if (current.attemptedAutoLocate || current.permissionDenied || current.coords) return

  const persisted = readPersistedLocationState()
  if (!persisted) return

  useUserDeviceLocationStore.setState(
    {
      coords: persisted.coords,
      permissionDenied: persisted.permissionDenied ?? false,
      attemptedAutoLocate: persisted.attemptedAutoLocate ?? false,
    },
    false,
    'hydrateFromSession'
  )
}

export const useUserDeviceLocationStore = create<UserDeviceLocationStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        locateUser: async (options) => {
          hydrateUserDeviceLocationFromSession()

          const { force = false } = options ?? {}
          const state = get()

          // Once per browser session: do not re-prompt after deny/dismiss/prior attempt.
          // force=true is reserved for an explicit user action (e.g. "Use my location").
          if (!force) {
            if (state.permissionDenied) return undefined
            if (state.coords) return state.coords
            if (state.attemptedAutoLocate) return undefined
          }

          set({ loading: true, error: undefined, attemptedAutoLocate: true }, false, 'locateUser/start')

          if (typeof navigator === 'undefined' || !navigator.geolocation) {
            const error = 'Geolocation not supported'
            set({ loading: false, error }, false, 'locateUser/notSupported')
            return undefined
          }

          // If the Permissions API already knows geolocation is denied, skip the prompt.
          if (!force && typeof navigator.permissions?.query === 'function') {
            try {
              const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
              if (status.state === 'denied') {
                set(
                  {
                    loading: false,
                    permissionDenied: true,
                    error: 'User denied the request for Geolocation.',
                  },
                  false,
                  'locateUser/permissionApiDenied'
                )
                return undefined
              }
            } catch {
              // permissions.query is not supported for geolocation in all browsers
            }
          }

          return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const coords: Coords = {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                }
                set(
                  {
                    loading: false,
                    coords,
                    permissionDenied: false,
                    error: undefined,
                  },
                  false,
                  'locateUser/success'
                )
                resolve(coords)
              },
              (err) => {
                let error = 'Unknown error'
                let permissionDenied = false
                switch (err.code) {
                  case err.PERMISSION_DENIED:
                    error = 'User denied the request for Geolocation.'
                    permissionDenied = true
                    break
                  case err.POSITION_UNAVAILABLE:
                    error = 'Location information is unavailable.'
                    break
                  case err.TIMEOUT:
                    error = 'The request to get user location timed out.'
                    break
                }
                set({ loading: false, error, permissionDenied }, false, 'locateUser/error')
                resolve(undefined)
              },
              // Cache a recent reading so follow-up calls (same session) stay quiet.
              { enableHighAccuracy: false, timeout: 5000, maximumAge: 300_000 }
            )
          })
        },
      }),
      createPersistOptions<UserDeviceLocationStore, 'coords' | 'permissionDenied' | 'attemptedAutoLocate'>({
        name: PERSIST_NAME,
        pick: ['coords', 'permissionDenied', 'attemptedAutoLocate'],
        storageType: 'session',
      })
    ),
    { name: 'UserDeviceLocationStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

/**
 * Utility function to get the user device location
 * @returns The user device location.
 */
export function getUserDeviceLocation(): Coords | undefined {
  hydrateUserDeviceLocationFromSession()
  return useUserDeviceLocationStore.getState().coords
}
